import { Inject, Injectable } from '@angular/core';
import { Observable, of, combineLatest, from, concat } from 'rxjs';
import { catchError, concatMap, last, map, take, tap } from 'rxjs/operators';

import { OFFSET_POSITION_NUMBER_CALC } from 'app/constants/filters.data';
import { absoluteValue } from 'app/utils/absolute-value';
import { consolidateLineUpdates } from 'app/utils/consolidate-line-updates';
import { extractDocVersion } from 'app/utils/document-version';
import { getLineProductName } from 'app/utils/get-line-product-name';
import { isBomSubLine } from 'app/utils/is-bom-sub-line';
import { isExternalArticleLine } from 'app/utils/is-external-article-line';
import { isGroupableLine } from 'app/utils/is-groupable-line';
import { mapConfigurationVariable } from 'app/utils/map-configuration-variable';
import { setCustomPropertyValueImmutable } from 'app/utils/set-custom-property-value';
import { mergeOfferPricingState } from 'app/offer/offer-utils';
import { getMultipleOfTen, isMultipleOfTen } from 'app/utils/numbers-utils';
import {
  AppConfigurationSection as ConfigurationSection,
  AppConfigurationVariable as ConfigurationVariable,
  AppCustomLine as CustomLine,
  AppProductLine as ProductLine,
  AppHeaderLine as HeaderLine,
  AppCustomPropertyInputArbonia as CustomPropertyInputArbonia,
  ConfigurationBooleanValue,
  LineCustomPropertyKeyType,
  Offer,
  OfferProductPosition,
  SnkLineId,
  SnkProduct,
  AppQuote as Quote
} from 'graphql/models/extended';
import {
  DocumentVersionInput,
  ImportLinesInput,
  LineInputType,
  MaterialAndEanOutput,
  VariableAssignmentInput
} from 'graphql/models/generated';
import { SelectorLine, SelectorVariable } from 'graphql/models/product-selector.model';
import { AddCustomPositionMutation } from 'graphql/mutations/add-custom-position';
import { AddCustomPositionCustomMutation } from 'graphql/mutations/add-custom-position-custom';
import { AddHeadingMutation } from 'graphql/mutations/add-heading';
import { AddProductPositionMutation } from 'graphql/mutations/add-product-position';
import { AddProductPositionCustomMutation } from 'graphql/mutations/add-product-position-custom';
import { CopyPositionsCustomMutation } from 'graphql/mutations/copy-positions-custom';
import { DeletePositionsMutation } from 'graphql/mutations/delete-positions';
import { SetCustomPropertyForLineMutation } from 'graphql/mutations/set-custom-property-for-line';
import { ToggleAlternativePositionMutation } from 'graphql/mutations/toggle-alternative-position';
import { UpdateAllPositionFieldsMutation } from 'graphql/mutations/update-all-position-fields';
import { UpdateLineAssignmentsMutation } from 'graphql/mutations/update-line-assignments';
import { UpdatePositionDescriptionMutation } from 'graphql/mutations/update-position-description';
import { UpdatePositionQuantityMutation } from 'graphql/mutations/update-position-quantity';
import { UpdateSelectorAssignmentMutation } from 'graphql/mutations/update-selector-assignment';
import { UpdateSelectorAssignmentsMutation } from 'graphql/mutations/update-selector-assignments';
import { MaterialAndEanCodesQuery } from 'graphql/queries/material-ean-codes';
import { PositionCustomPropertiesQuery } from 'graphql/queries/position-custom-properties';
import { ProductPositionQuery } from 'graphql/queries/product-position';
import { ProductPositionBomItemsQuery } from 'graphql/queries/product-position-bom-items';
import { SelectorFilterQuery } from 'graphql/queries/selector-filter';
import { SelectorPositionQuery } from 'graphql/queries/selector-position';
import { MassActionsMod } from 'models/layout-mod.model';
import { MiniCartPositions } from 'models/mini-cart';
import { OfferBase } from 'models/offer.model';
import { LineSubtypePropVal } from 'models/position.model';
import { ProductSelector } from 'models/product-selector-item.model';
import { ValueTextType } from 'models/value-text.model';
import { ValueTextsService } from 'shared/services//value-texts.service';
import { OfferMapperService } from 'shared/services/offer-mapper.service';
import { OfferPositionProductService } from 'shared/services/offer-position-product.service';
import { OfferSectionService } from 'shared/services/offer-section.service';
import { OfferService } from 'shared/services/offer.service';
import { PositionCustomPropertiesService } from 'shared/services/position-custom-properties.service';
import { PricingService } from 'shared/services/pricing.service';
import { StoreService } from 'shared/services/store.service';
import { PositionProductAssignmentsService } from 'shared/services/position-product-assignments.service';
import { isProductLine } from 'app/utils/is-product-line'
import { OfferPricing } from 'models/pricing.model'

@Injectable({
  providedIn: 'root'
})
export class OfferPositionService {
  isDealer = false;

  constructor(
    @Inject('datamodel') private datamodel: Record<string, any>,
    private productPositionQuery: ProductPositionQuery,
    private selectorPositionQuery: SelectorPositionQuery,
    private productPositionBomItemsQuery: ProductPositionBomItemsQuery,
    private selectorFilterQuery: SelectorFilterQuery,
    private positionCustomPropertiesQuery: PositionCustomPropertiesQuery,
    private addProductPositionMutation: AddProductPositionMutation,
    private addProductPositionCustomMutation: AddProductPositionCustomMutation,
    private addCustomPositionMutation: AddCustomPositionMutation,
    private addCustomPositionCustomMutation: AddCustomPositionCustomMutation,
    private toggleAlternativePositionMutation: ToggleAlternativePositionMutation,
    private addHeadingMutation: AddHeadingMutation,
    private updateSelectorAssignmentMutation: UpdateSelectorAssignmentMutation,
    private updateSelectorAssignmentsMutation: UpdateSelectorAssignmentsMutation,
    private updatePositionDescriptionMutation: UpdatePositionDescriptionMutation,
    private updatePositionQuantityMutation: UpdatePositionQuantityMutation,
    private updateAllPositionFieldsMutation: UpdateAllPositionFieldsMutation,
    private deletePositionsMutation: DeletePositionsMutation,
    private copyPositionsCustomMutation: CopyPositionsCustomMutation,
    private offerService: OfferService,
    private posCustomPropertiesService: PositionCustomPropertiesService,
    private posProductAssignmentsService: PositionProductAssignmentsService,
    private storeService: StoreService,
    private pricingService: PricingService,
    private offerMapperService: OfferMapperService,
    private offerSectionService: OfferSectionService,
    private offerPositionProductService: OfferPositionProductService,
    private valueTextsService: ValueTextsService,
    private posCustomPropsService: PositionCustomPropertiesService,
    private setCustomPropertyForLineMutation: SetCustomPropertyForLineMutation,
    private updateLineAssignmentsMutation: UpdateLineAssignmentsMutation,
    private materialAndEanCodesQuery: MaterialAndEanCodesQuery
  ) {
    this.init();
  }

  private init(): void {
    this.storeService.isCurrentAccountDealer().subscribe((isDealer) => {
      this.isDealer = isDealer;
    });
  }

  hasIncompatibleValue(item?: SelectorVariable | ConfigurationVariable): boolean {
    return ((item && item.values) || []).some((v) => v.value === ConfigurationBooleanValue.TRUE && v.incompatible);
  }

  getPosition(
    offer: OfferBase,
    positionId: string,
    withVariables = true,
    withPricing = true
  ): Observable<ProductLine | CustomLine> {
    const variables = {
      documentVersion: extractDocVersion(offer),
      lineId: positionId,
      withVariables,
      withPricing
    };

    // When watching the query, the value is being changed on each mutation.
    // Is it because we are using the same fragment in query and mutation?
    // return this.productPositionQuery.watch(variables).valueChanges;
    return this.productPositionQuery.fetch(variables, { fetchPolicy: 'no-cache' }).pipe(
      map((r) => r.data?.quotes?.quote?.line as ProductLine | CustomLine),
      map((pos) => (pos ? (this.mapPosition(pos) as ProductLine | CustomLine) : pos)),
      take(1)
    );
  }

  getPositionBomItems(documentVersion: DocumentVersionInput): Observable<Array<ProductLine | CustomLine>> {
    const variables = { documentVersion };

    return this.productPositionBomItemsQuery
      .fetch(variables, { fetchPolicy: 'no-cache' })
      .pipe<Array<ProductLine | CustomLine>, Array<ProductLine | CustomLine>>(
        map((r) => r.data?.quotes?.quote?.lines as Array<ProductLine | CustomLine>),
        take(1)
      );
  }

  getArticleProductPosition(offer: OfferBase, positionId: string): Observable<OfferProductPosition> {
    return this.getPosition(offer, positionId).pipe<OfferProductPosition>(
      map((line: ProductLine | CustomLine) => {
        let position = { ...line } as CustomLine;

        return {
          product: this.offerPositionProductService.getProductBaseFromLine(line),
          positions: [position]
        }
      })
    );
  }

  getFreeArticleProductPosition(offer: OfferBase, positionId: string): Observable<OfferProductPosition> {
    return this.getPosition(offer, positionId).pipe<OfferProductPosition>(
      map((line: ProductLine | CustomLine) => {
        let position = { ...line } as CustomLine;

        return {
          product: this.offerPositionProductService.getProductBaseFromLine(line),
          positions: [position]
        }
      })
    );
  }

  getLooseArticleProductPosition(offer: OfferBase, positionId: string): Observable<OfferProductPosition> {
    return this.getPosition(offer, positionId).pipe<OfferProductPosition>(
      map((line: ProductLine | CustomLine) => {
        let position = { ...line } as ProductLine;

        return {
          product: this.offerPositionProductService.getProductBaseFromLine(line),
          positions: [position]
        }
      })
    );
  }

  getCustomPropertiesForOfferPositions(offer: OfferBase): Observable<Array<ProductLine | CustomLine>> {
    const variables = {
      documentVersion: extractDocVersion(offer)
    };

    return this.positionCustomPropertiesQuery
      .fetch(variables, { fetchPolicy: 'no-cache' })
      .pipe<Array<ProductLine | CustomLine>, Array<ProductLine | CustomLine>>(
        map((r) => r.data?.quotes?.quote?.lines as Array<ProductLine | CustomLine>),
        take(1)
      );
  }

  sortPositions(positions: ProductLine[]): ProductLine[] {
    return this.offerService.sortPositions(positions);
  }

  setPositionNumbers(lineUpdates?: LineInputType[]): Observable<string[]> {
    return this.storeService.getCurrentOffer().pipe(
      concatMap((currentOffer: OfferBase) =>
        this.getPositionNumbers(currentOffer, lineUpdates).pipe(
          tap((positionNumbers) => {
            this.storeService.setCurrentOffer({
              ...currentOffer,
              positionNumbers
            });
          })
        )
      ),
      take(1)
    );
  }

  getPositionNumbers(offer: OfferBase, lineUpdates?: LineInputType[]): Observable<string[]> {
    if (!offer) return of(['']);

    return this.getCustomPropertiesForOfferPositions(offer).pipe(
      map((positions) => this.posCustomPropertiesService.extractPositionNumbers(positions, lineUpdates)),
      take(1)
    );
  }

  getProductName(line: ProductLine): string {
    const pos = line;
    if (!pos.product) {
      pos.product = this.offerPositionProductService.getProductBaseFromLine(line) as SnkProduct;
    }

    return pos.product?.name && pos.product.name.toLowerCase().includes('sonstige')
      ? 'Sonstige'
      : pos.product.productName || pos.product.name;
  }

  findIndexForNumber(arr: string[], number: string): number {
    const idx = arr.findIndex((element) => Number(element) > Number(number));

    return idx === -1 ? arr.length : idx;
  }

  findProductGroupIndexByPositionId(productPositions: OfferProductPosition[], groupId: string): number {
    return productPositions.findIndex((product) => product.positions.some((pos) => pos.id === groupId));
  }

  findProductPositionIndexById(productPositions: OfferProductPosition[], index: number, id: string): number {
    return productPositions[index].positions.findIndex((pos) => pos.id === id);
  }

  sortPositionsByPositionNumber(positions: ProductLine[]): void {
    positions.sort((a, b) => parseInt(a.positionNumber || '0', 10) - parseInt(b.positionNumber || '0', 10));
  }

  sortProductPositions(
    prodPositions: OfferProductPosition<ProductLine | CustomLine>[]
  ): OfferProductPosition<ProductLine | CustomLine>[] {
    return prodPositions.sort((a, b) => {
      let ret = 0;

      if (a.positions && a.positions.length > 0 && b.positions && b.positions.length > 0) {
        ret = parseInt(a.positions[0].positionNumber || '0', 10) - parseInt(b.positions[0].positionNumber || '0', 10);
      }

      return ret;
    });
  }

  changePositionAndCustomProperty(positions: LineInputType[], line: ProductLine, value: string): void {
    const key = LineCustomPropertyKeyType.LineID;

    positions.push({
      id: line.id,
      properties: [{ key, value, propertyType: 'String' }]
    });

    setCustomPropertyValueImmutable(line, key, value);
  }

  updateRelationsInGroup(positions: LineInputType[], group: OfferProductPosition<ProductLine | CustomLine>): void {
    group.positions.forEach((position, index) => {
      if (index === 0) {
        this.changePositionAndCustomProperty(positions, position as ProductLine, SnkLineId.Parent);
      } else {
        this.changePositionAndCustomProperty(positions, position as ProductLine, group.positions[0].id);
      }
    });
  }

  removeOrUpdateGroup(
    prodPositions: OfferProductPosition<ProductLine | CustomLine>[],
    index: number,
    group: OfferProductPosition<ProductLine | CustomLine>,
    positionId: string,
    isParent: boolean,
    positions: LineInputType[]
  ): void {
    if (group.positions.length === 1) {
      // remove group if last element
      this.removeProductGroup(prodPositions, index);
    } else {
      // eslint-disable-next-line no-param-reassign
      group.positions = group.positions.filter((pos) => pos.id !== positionId);
      if (isParent) {
        // update old group positions if parent moved
        this.updateRelationsInGroup(positions, group);
      }
    }
  }

  private removeProductGroup(prodPositions: OfferProductPosition<ProductLine | CustomLine>[], index: number): void {
    prodPositions.splice(index, 1);
  }

  private combineOfferGroups(prodPositions: OfferProductPosition[], lineUpdates: LineInputType[]): void {
    this.sortProductPositions(prodPositions);
    let nextIndex: number;
    let lastIndex = prodPositions.length;
    for (let i = 0; i < lastIndex; ++i) {
      nextIndex = i + 1;
      if (this.canCombineProductGroups(i, nextIndex, prodPositions)) {
        prodPositions[i].positions = this.sortPositions([
          ...(prodPositions[i].positions as ProductLine[]),
          ...(prodPositions[nextIndex].positions as ProductLine[])
        ]);
        this.updateRelationsInGroup(lineUpdates, prodPositions[i]);
        this.removeProductGroup(prodPositions, nextIndex);
        i = i - 1 || 0;
      }
      lastIndex = prodPositions.length;
    }

    const groupsIds = lineUpdates
    .filter((line) =>
      line.properties?.some((prop) => prop.value === SnkLineId.Parent)
    )
    .map((l) =>
      this.findProductGroupIndexByPositionId(
        prodPositions,
        l.id
      )
    )
    .filter((elem, index, self) => index === self.indexOf(elem));

    if (groupsIds) {
      this.updateProductPositionsAssignments(prodPositions, groupsIds).subscribe(() => {});
    }
  }

  updateProductPositionsAssignments(
    prodPositions: OfferProductPosition[],
    groupIndexes: number[]
  ): Observable<ProductLine> {
    const obs$ = prodPositions.map((productPosition, index) => {
      const groupPositions =
        productPosition.product &&
        productPosition.product.isConfigurable &&
        groupIndexes.includes(index)
          ? (productPosition.positions as ProductLine[])
          : [];
      let posIndex = 0;

      return from(groupPositions).pipe(
        map((pos) => {
          const positions = [
            ...prodPositions[index].positions,
          ] as ProductLine[];
          const product = this.posProductAssignmentsService.mapPositionProductAssignmentsDifferences(
            pos,
            positions[0]
          );
          return { ...pos, product } as ProductLine;
        }),
        concatMap((pos) =>
          this.valueTextsService.mapValueTextsToProductLine(
            pos,
            ValueTextType.CalculationSheet
          )
        ),
        map((pos) => {
          if (pos.product) {
            if (posIndex === 0) {
              ((
                prodPositions[
                  index
                  ] as OfferProductPosition<ProductLine>
              ).product as SnkProduct).productAssignments = pos.product.productAssignments;
            }

            (
              prodPositions[
                index
                ] as OfferProductPosition<ProductLine>
            ).positions[posIndex].product = { ...pos.product };
          }

          posIndex += 1;
          return pos;
        })
      );
    });

    return concat(...obs$);
  }

  private canCombineProductGroups(firstIndex: number, nextIndex: number, groups: OfferProductPosition[]): boolean {
    if (firstIndex >= 0 && groups[firstIndex] && groups[nextIndex]) {
      const prevLine = groups[firstIndex].positions[0] as ProductLine;
      const nextLine = groups[nextIndex].positions[0] as ProductLine;

      return this.canCombineGroupsPositions(prevLine, nextLine);
    }

    return false;
  }

  private canCombineGroupsPositions(firstPosition: ProductLine, nextPosition: ProductLine): boolean {
    const prevProduct = this.getProductName(firstPosition);
    const nextProduct = this.getProductName(nextPosition);

    return isGroupableLine(firstPosition) && isGroupableLine(nextPosition) && prevProduct === nextProduct;
  }

  private getParentsPositionsNumbers(prodPositions: OfferProductPosition[], excludeId?: string): string[] {
    return prodPositions
      .map((product) => product.positions)
      .flat()
      .filter(
        (pos) =>
          (this.posCustomPropsService.isParentLine(pos) || this.posCustomPropsService.isDetachedLine(pos)) &&
          pos.id !== excludeId
      )
      .map((pos) => pos.positionNumber) as string[];
  }

  reorderPositions(offer: Offer): LineInputType[] {
    const step = 10;
    let orderIndex = 0;
    let currentNumber = step;
    const linesUpdate: LineInputType[] = [];
    const linesOffset: LineInputType[] = [];
    const productPositions = offer.productPositions;

    const originLines = productPositions
      .slice()
      .map((product) => product.positions)
      .flat()
      .map((pos): { id: string; originNumber?: string } => ({
        id: pos.id,
        originNumber: pos.positionNumber
      }));

    offer.productPositions.forEach((product) => {
      product.positions.forEach((position) => {
        const originalNumber = position.positionNumber;
        // eslint-disable-next-line no-param-reassign
        position.positionNumber = currentNumber.toString();
        if (originalNumber !== position.positionNumber) {
          setCustomPropertyValueImmutable(
            position,
            LineCustomPropertyKeyType.ArticleID,
            currentNumber.toString()
          );
          linesUpdate.push({
            id: position.id,
            properties: [
              {
                key: LineCustomPropertyKeyType.ArticleID,
                value: position.positionNumber,
                propertyType: 'String'
              }
            ]
          });

          const overlapIndex = originLines.findIndex((pos) => pos.originNumber === position.positionNumber);
          if (overlapIndex !== -1 && overlapIndex > orderIndex) {
            linesOffset.push({
              id: originLines[overlapIndex].id,
              properties: [
                {
                  key: LineCustomPropertyKeyType.ArticleID,
                  value: this.getTempNumberForOffset(originLines[overlapIndex].originNumber || orderIndex.toString()),
                  propertyType: 'String'
                }
              ]
            });
          }
        }
        currentNumber += step;
        orderIndex += 1;
      });
    });

    return [...linesOffset, ...linesUpdate];
  }

  /**
   * Moves all `positionNumbers` of the `positions` within the given range by `amount`.\
   * Stops if there is a gap that is equal to `amount` and leaves all following
   * positions untouched.
   *
   * **Expects `positions` to be sorted by `positionNumber`**
   */
  movePosNumBy(
    productPositions: OfferProductPosition<ProductLine | CustomLine>[],
    newNum: number,
    oldNum = Infinity,
    amount = 10,
    changedLineId: string
  ): LineInputType[] {
    const moveToTop = newNum < oldNum;
    const lineUpdates: LineInputType[] = [];
    let lastPosNumResult = newNum;
    let posNum: number;
    let line: ProductLine | CustomLine;
    let lines = productPositions.map((product) => product.positions).flat() as ProductLine[];
    let highPosNumResult = (moveToTop ? oldNum : Number(lines[lines.length - 1].positionNumber || '0'));

    const noOverlap = () => lastPosNumResult < posNum;

    this.sortPositionsByPositionNumber(lines);

    // if line is moving down we want to have
    // only lines in the given range sorted
    if (!moveToTop) {
      lines = lines.filter(
        (pos) =>
          pos.id !== changedLineId &&
          Number(pos.positionNumber || '0') >= newNum
      );
    }

    for (line of lines) {
      posNum = Number(line.positionNumber || '0');
      // stop if we leave the range or there is a big enough gap
      if (
        (moveToTop && (posNum >= oldNum || noOverlap()))
        || (!moveToTop && (highPosNumResult < posNum || noOverlap())) //
      ) {
        return (moveToTop ? lineUpdates : lineUpdates.reverse());
      }

      // only affect lines in the given range
      if (
        (moveToTop && posNum >= newNum) ||
        (!moveToTop && posNum <= highPosNumResult)
      ) {
        lastPosNumResult = posNum + amount;
        this.updatePositionPosNumberAndProperties(lineUpdates, line, lastPosNumResult);
      }
    }

    return (moveToTop ? lineUpdates : lineUpdates.reverse());
  }

  movePosition(
    productPositions: OfferProductPosition<ProductLine | CustomLine>[],
    changedLine: ProductLine,
    targetPosNum: number,
    canMove: boolean
  ): LineInputType[] {
    const oldPosNum = Number(changedLine.positionNumber || '0');
    let lineUpdates: LineInputType[] = [];
    // target position number converted to a power of ten
    const targetPosNumPow10 = getMultipleOfTen(targetPosNum);

    // move all affected positions either down or up
    if (!canMove) {
      lineUpdates = this.movePosNumBy(productPositions, targetPosNumPow10, oldPosNum, 10, changedLine.id);
    }

    this.updatePositionPosNumberAndProperties(
      lineUpdates,
      changedLine,
      targetPosNumPow10,
      oldPosNum > targetPosNumPow10
    );

    return lineUpdates;
  }

  private getTempNumberForOffset(number: string): string {
    return (OFFSET_POSITION_NUMBER_CALC + Number(number)).toString();
  }

  updatePositionPosNumberAndProperties(
    changes: LineInputType[],
    position: ProductLine | CustomLine,
    currentNumber: number,
    reverseOrder?: boolean
  ): void {
    const posNum = currentNumber.toString();

    // eslint-disable-next-line no-param-reassign
    position.positionNumber = posNum;
    setCustomPropertyValueImmutable(position, LineCustomPropertyKeyType.ArticleID, posNum);

    if (reverseOrder) {
      changes.reverse();
    }

    changes.push({
      id: position.id,
      properties: [
        {
          key: LineCustomPropertyKeyType.ArticleID,
          value: posNum,
          propertyType: 'String'
        }
      ]
    });
  }

  findProductPositionLine(
    productPositions: OfferProductPosition<ProductLine | CustomLine>[],
    id: string
  ): ProductLine | CustomLine | undefined {
    let pos: OfferProductPosition<ProductLine | CustomLine>;
    let line: ProductLine | CustomLine;

    for (pos of productPositions) {
      for (line of pos.positions) {
        if (line.id === id) {
          return line;
        }
      }
    }

    return undefined;
  }

  positionNumberAvailable(
    offer: OfferBase,
    productPositions: OfferProductPosition<ProductLine | CustomLine>[],
    number: number
  ): boolean {
    const positions = productPositions.map((product) => product.positions).flat() as ProductLine[];
    const num = getMultipleOfTen(number).toString();
    const posNums = positions.map((pos) => pos.positionNumber || '');
    posNums.concat(offer.positionNumbers || []);

    return !posNums.includes(num);
  }

  applyPositionUpdates(offer: Offer | OfferBase, lineUpdates: LineInputType[]): void {
    const lines = (offer as Offer).lines;
    const productPositions = (offer as Offer).productPositions;

    let articleIdUpdate: CustomPropertyInputArbonia | undefined;
    let line: ProductLine | CustomLine | undefined;

    lineUpdates.forEach((update) => {
      articleIdUpdate = update.properties?.find((z) => z.key === LineCustomPropertyKeyType.ArticleID) as
        | CustomPropertyInputArbonia
        | undefined;
      if (articleIdUpdate) {
        line = lines?.find((l) => l.id === update.id);
        if (line) {
          line.positionNumber = articleIdUpdate.value;
          setCustomPropertyValueImmutable(line, LineCustomPropertyKeyType.ArticleID, articleIdUpdate.value);
        }

        line = this.findProductPositionLine(productPositions, update.id);
        if (line) {
          line.positionNumber = articleIdUpdate.value;
          setCustomPropertyValueImmutable(line, LineCustomPropertyKeyType.ArticleID, articleIdUpdate.value);
        }
      }
      this.storeService.setOfferUpdate({
        productPositionsUpdate: (offer as Offer).productPositions
      });
    });
  }

  updatePositionNumber(offer: Offer | OfferBase, positionId: string, positionNumber: string): Observable<Offer> {
    let lineUpdates: LineInputType[] = [];
    let lineOffset: ProductLine | CustomLine | undefined;
    let canMove = true;
    const posNum = Number(positionNumber);
    const prodPositions = (offer as Offer).productPositions;
    let changedPosition = prodPositions
      .map((product) => product.positions.find((pos) => pos.id === positionId))
      .find(Boolean) as ProductLine;

    // if number is with in between 10,
    // manually resort positions and then
    // add the position at that place
    const isNotMultipleOf10 = !isMultipleOfTen(posNum);
    if (isNotMultipleOf10) {
      const oldNum = Number(changedPosition.positionNumber || '0');
      const newNum = Number(positionNumber);
      const moveDown = oldNum < newNum;
      const movedBy = absoluteValue(newNum - oldNum);
      // this should happen only when the changed position should not change place
      if (!moveDown && movedBy > 0 && movedBy < 10) {
        return of(offer) as Observable<Offer>;
      }
      // check if place available for number
      canMove = this.positionNumberAvailable(offer, prodPositions, posNum);
    }

    // when we need to move a position to a place occupied
    // by another position, first of all we need to change
    // number of current position to temporary number,
    // to be able to change the position numbers of affected
    // lines that are affected by this action
    if (isNotMultipleOf10 && !canMove) {
      lineOffset = changedPosition;
    }

    lineUpdates = this.movePosition(prodPositions, changedPosition, posNum, canMove);

    // productPositions exist only in Calculation Sheet
    if ('productPositions' in offer && changedPosition) {
      this.changePlaceForProductPositionsTable(
        prodPositions,
        changedPosition,
        lineUpdates,
        positionId,
        positionNumber,
        isNotMultipleOf10
      );
    }

    const consolidated = consolidateLineUpdates(lineUpdates);

    // send to backend
    return this.savePositionNumber(offer, consolidated, lineOffset);
  }

  private changePlaceForProductPositionsTable(
    prodPositions: OfferProductPosition<ProductLine | CustomLine>[],
    changedPosition: ProductLine,
    lineUpdates: LineInputType[],
    positionId: string,
    positionNumber: string,
    isNotMultipleOf10: boolean
  ): void {
    let prevPosNum = changedPosition?.positionNumber || '';
    if (isNotMultipleOf10) {
      prevPosNum = positionNumber;
    }

    const oldGroupIndex = prodPositions.findIndex((product) => product.positions.some((pos) => pos.id === positionId));
    const parentLinesPosNums = this.getParentsPositionsNumbers(prodPositions);
    const newGroupIndex = this.findIndexForNumber(parentLinesPosNums, positionNumber) - 1;
    const isParent = parentLinesPosNums.includes(prevPosNum);
    const oldGroup = prodPositions[oldGroupIndex];
    const newGroup = prodPositions[newGroupIndex];

    // Update the changed position with new positionNumber
    changedPosition = { ...changedPosition, positionNumber };

    if (!changedPosition.product || (changedPosition.product && !changedPosition.product.isConfigurable)) {
      const product = this.offerPositionProductService.getProductBaseFromLine(changedPosition) as SnkProduct;
      changedPosition = { ...changedPosition, product };
    }

    setCustomPropertyValueImmutable(changedPosition, LineCustomPropertyKeyType.ArticleID, positionNumber);

    if (newGroupIndex === -1) {
      // console.warn('should add new group as first group');
      // Set position to be as a parent
      this.changePositionAndCustomProperty(lineUpdates, changedPosition, SnkLineId.Parent);
      this.removeOrUpdateGroup(prodPositions, oldGroupIndex, oldGroup, positionId, isParent, lineUpdates);
      // Add on top as first position
      prodPositions.splice(0, 0, {
        product: changedPosition.product,
        positions: [changedPosition]
      });
    } else if (oldGroupIndex === newGroupIndex) {
      // console.warn('should move in same group');
      oldGroup.positions = oldGroup.positions.map((pos) =>
        pos.id === changedPosition.id ? { ...changedPosition } : pos
      ) as ProductLine[];
      this.sortPositionsByPositionNumber(oldGroup.positions as ProductLine[]);
      // when in same group if it's parent it needs to update relationships if not it should not
      this.updateRelationsInGroup(lineUpdates, oldGroup);
    } else {
      // console.warn(`should move group from ${oldGroupIndex} to ${newGroupIndex}`);
      // check if product names match or should break the group
      if (this.canCombineGroupsPositions(changedPosition, newGroup.positions[0] as ProductLine)) {
        // console.warn('Products match -> merge with', newGroup);
        this.removeOrUpdateGroup(prodPositions, oldGroupIndex, oldGroup, positionId, isParent, lineUpdates);
        // Add new position in new Group
        newGroup.positions.push(changedPosition);
        // sort new group by position number
        this.sortPositionsByPositionNumber(newGroup.positions as ProductLine[]);
        // update newGroup relationships
        this.updateRelationsInGroup(lineUpdates, newGroup);
      } else {
        // console.warn('Should break the group', newGroup);
        const newGroupPosNums = newGroup.positions.map((p) => p.positionNumber) as string[];
        const splitIdx = newGroupPosNums.findIndex((posNum) => Number(posNum) > Number(positionNumber));

        if (splitIdx !== -1) {
          // console.warn('Inside existing group -> Should split existing group');
          // Set position to be as a parent
          this.changePositionAndCustomProperty(lineUpdates, changedPosition, SnkLineId.Parent);
          this.removeOrUpdateGroup(prodPositions, oldGroupIndex, oldGroup, positionId, isParent, lineUpdates);
          // Add after newGroup index
          prodPositions.splice(newGroupIndex + 1, 0, {
            product: changedPosition.product,
            positions: [changedPosition]
          });

          // Remove children from old position
          let newGroupFromChildren = newGroup.positions.slice(splitIdx) as ProductLine[];
          newGroup.positions = newGroup.positions.filter((pos) => !newGroupFromChildren.some((p) => p.id === pos.id));

          const addNewGroupToIndex = newGroupIndex + 2;

          // check if next group can combine with new group
          if (
            prodPositions[addNewGroupToIndex] &&
            this.canCombineGroupsPositions(
              newGroupFromChildren[0] as ProductLine,
              prodPositions[addNewGroupToIndex].positions[0] as ProductLine
            )
          ) {
            newGroupFromChildren = [
              ...newGroupFromChildren,
              ...(prodPositions[addNewGroupToIndex].positions as ProductLine[])
            ];

            this.removeProductGroup(prodPositions, addNewGroupToIndex);
          }

          const newChildGroupProduct = this.offerPositionProductService.getProductBaseFromLine(
            newGroupFromChildren[0]
          ) as SnkProduct;
          // Set position to be as a parent
          const newChildGroup = {
            product: { ...newGroup.product, newChildGroupProduct },
            positions: this.sortPositions(newGroupFromChildren)
          };
          prodPositions.splice(addNewGroupToIndex, 0, newChildGroup);
          this.updateRelationsInGroup(lineUpdates, newChildGroup);
        } else {
          // console.warn('Between groups -> Should be its own separate group');
          // Set position to be as a parent
          this.changePositionAndCustomProperty(lineUpdates, changedPosition, SnkLineId.Parent);
          this.removeOrUpdateGroup(prodPositions, oldGroupIndex, oldGroup, positionId, isParent, lineUpdates);
          // Add after newGroup index
          prodPositions.splice(newGroupIndex + 1, 0, {
            product: changedPosition.product,
            positions: [changedPosition]
          });
        }
      }
    }
    this.combineOfferGroups(prodPositions, lineUpdates);
  }

  private savePositionNumber(
    offer: OfferBase,
    linesUpdate: LineInputType[],
    offsetLine?: ProductLine | CustomLine
  ): Observable<Offer> {
    if (!offsetLine || !offsetLine.positionNumber) {
      return this.savePositionNumberUpdate(offer, linesUpdate);
    }

    return this.setCustomPropertyForLine(
      offer,
      offsetLine.id,
      LineCustomPropertyKeyType.ArticleID,
      this.getTempNumberForOffset(offsetLine.positionNumber)
    ).pipe(
      concatMap(() => this.savePositionNumberUpdate(offer, linesUpdate)),
      take(1)
    );
  }

  private savePositionNumberUpdate(offer: OfferBase, linesUpdate: LineInputType[]): Observable<Offer> {
    return this.offerService.updateOffer(offer, undefined, linesUpdate).pipe(
      // persist locally
      concatMap(() =>
        this.setPositionNumbers().pipe(
          map((positionNumbers) => {
            this.applyPositionUpdates(offer, linesUpdate);

            return {
              ...offer,
              positionNumbers
            } as Offer;
          })
        )
      ),
      take(1)
    );
  }

  setMiniCartPositions(_positionIds: string[]): Observable<MiniCartPositions> {
    return this.storeService.getMiniCartPositions().pipe(
      take(1),
      map((_mcPositions) => {
        const updatedMcPositions = {
          // new: [...new Set([...((mcPositions && mcPositions.new) || []), ...positionIds])]
        };
        this.storeService.setMiniCartPositions(updatedMcPositions);

        return updatedMcPositions;
      })
    );
  }

  setParentChildRelations(offer: OfferBase, relations: { id: string; value: string }[]): Observable<Offer> {
    const updatePositions: LineInputType[] = relations.map((rel) => ({
      id: rel.id,
      properties: [
        {
          key: LineCustomPropertyKeyType.LineID,
          value: rel.value,
          propertyType: 'String'
        }
      ]
    }));

    return this.offerService.updateOffer(offer, undefined, updatePositions).pipe(take(1));
  }

  setOptionalPositions(offer: OfferBase, positionIds: string[]): Observable<Offer> {
    const positions: LineInputType[] = positionIds.map((positionId) => ({
      id: positionId,
      properties: [
        {
          key: LineCustomPropertyKeyType.Optional,
          value: 'True',
          propertyType: 'String'
        }
      ]
    }));

    return this.offerService.updateOffer(offer, undefined, positions).pipe(take(1));
  }

  unsetOptionalPositions(offer: OfferBase, positionIds: string[]): Observable<Offer> {
    const positions: LineInputType[] = positionIds.map((positionId) => ({
      id: positionId,
      properties: [
        {
          key: LineCustomPropertyKeyType.Optional,
          value: '',
          propertyType: 'String'
        }
      ]
    }));

    return this.offerService.updateOffer(offer, undefined, positions);
  }

  updatePositionDescription(offer: OfferBase, positionId: string, description: string): Observable<Offer> {
    const variables = {
      documentVersion: extractDocVersion(offer),
      lineId: positionId,
      description
    };

    return this.updatePositionDescriptionMutation
      .mutate(variables, { fetchPolicy: 'no-cache' })
      .pipe<Quote, Offer, Offer, Offer>(
        map((r) => r.data?.quotes?.modify?.line?.setDescription as Quote),
        map((quote: Quote) => this.offerService.mapQuoteToOffer(quote)),
        tap((updatedOffer: Offer) =>
          this.storeService.setOfferUpdate({
            status: updatedOffer.status,
            snkModifier: updatedOffer.snkModifier
          })
        ),
        take(1)
      );
  }

  updatePositionQuantity(
    offer: OfferBase,
    positionId: string,
    quantity: number,
    withPricing: boolean = true,
    withQuotePricing: boolean = true
  ): Observable<Offer> {
    const variables = {
      documentVersion: extractDocVersion(offer),
      lineId: positionId,
      quantity,
      withPricing,
      withQuotePricing
    };

    const isMiniCartAction = withPricing;

    return this.updatePositionQuantityMutation.mutate(variables, { fetchPolicy: 'no-cache' }).pipe(
      map((r) => r.data?.quotes?.modify?.line?.setQuantity as Quote),
      map((quote: Quote) => this.offerService.mapQuoteToOffer(quote)),
      concatMap((updatedOffer: Offer) =>
        combineLatest([
          of(updatedOffer),
          (isMiniCartAction
            ? this.pricingService.ensurePricesUpdated(updatedOffer, positionId, withPricing).pipe(
              map((pricingResponse) => {
                // Find the appropriate pricing for the correct product position
                const pricing = updatedOffer.productPositions
                .flatMap((productPosition) => productPosition.positions)
                .find((position) => position.id === positionId)?.pricing;

                // Use the found pricing or fallback to pricing from the response
                return {
                  id: positionId,
                  pricing: pricingResponse?.line?.pricing || pricing,
                  ...pricingResponse?.line // Include other line properties if needed
                };
              }))
            : of(updatedOffer).pipe(
              map((updatedOffer) => {
                const pos = updatedOffer.productPositions
                .flatMap((productPosition) => productPosition.positions)
                .find((position) => position.id === positionId);
                const linePricing = (updatedOffer.lines ? updatedOffer.lines.find((line) => line.id === positionId)?.pricing : pos?.pricing);

                // Use the found pricing or fallback to pricing from the response
                return {
                  id: positionId,
                  pricing: linePricing,
                  ...pos // Include other line properties if needed
                }
              })
            )
          )
        ]).pipe(
          map(([offer, updatedLine]) => {
            const mergedOffer = {
              ...offer,
              lines: (offer.lines || []).map((line) => (line.id === positionId ? { ...line, ...updatedLine } : line)),
              productPositions: (offer.productPositions || []).map((productPosition) => {
                const updatedPosition = productPosition.positions.find((position) => position.id === positionId);

                if (updatedPosition) {
                  // Replace only the pricing for the matching position
                  return {
                    ...productPosition,
                    positions: productPosition.positions.map((position) =>
                      position.id === positionId
                        ? { ...position, pricing: updatedLine.pricing } // Only update the pricing
                        : position
                    )
                  };
                }
                return productPosition; // Return unchanged productPosition
              })
            };

            let positionPricingUpdate;

            if (withPricing) {
              positionPricingUpdate = this.pricingService.extractPricingUpdate(mergedOffer as Required<Offer>);
            }

            let offerUpdate = mergeOfferPricingState({} as Offer, offer);

            offerUpdate = {
              ...offerUpdate,
              offerPricingUpdate: offer.pricing,
              status: offer.status,
              snkModifier: offer.snkModifier
            }

            if (positionPricingUpdate) {
              offerUpdate = {
                ...offerUpdate,
                positionPricingUpdate
              }
            }
            this.storeService.setOfferUpdate(offerUpdate);

            return { ...mergedOffer, positionPricingUpdate } as Offer;
          })
        )
      ),
      take(1)
    );
  }

  updateAllPositionFields(
    offer: OfferBase,
    positionId: string,
    quantity: number,
    description: string,
    positionNumber: string,
    descriptiveText: string,
    ProductID: string,
    LineSubtype?: string,
    CustomKurztext?: string,
    CustomDescription?: string,
    articleData?: string
  ): Observable<Offer> {
    const customDescriptionKey = this.getCustomLineDescriptionPropertyKey(LineSubtype);

    const properties: CustomPropertyInputArbonia[] = [
      {
        key: LineCustomPropertyKeyType.ArticleID,
        value: String(positionNumber),
        propertyType: 'String'
      },
      {
        key: LineCustomPropertyKeyType.DescriptiveText,
        value: descriptiveText || '',
        propertyType: 'String'
      },
      {
        key: LineCustomPropertyKeyType.ProductID,
        value: ProductID || '',
        propertyType: 'String'
      }
    ];

    if (LineSubtype) {
      properties.push({
        key: LineCustomPropertyKeyType.LineSubtype,
        value: LineSubtype,
        propertyType: 'String'
      });
    }
    if (CustomKurztext) {
      properties.push({
        key: LineCustomPropertyKeyType.CustomPositionskurztext,
        value: CustomKurztext,
        propertyType: 'String'
      });
    }
    if (CustomDescription && customDescriptionKey) {
      properties.push({
        key: customDescriptionKey,
        value: CustomDescription,
        propertyType: 'String'
      });
    }

    if (articleData) {
      properties.push({
        key: LineCustomPropertyKeyType.CustomConfigurationSnapshot,
        value: articleData,
        propertyType: 'String'
      });
    }

    const lines: LineInputType[] = [
      {
        id: positionId,
        properties
      }
    ];
    const variables = {
      documentVersion: extractDocVersion(offer),
      lineId: positionId,
      lines,
      quantity: Number(quantity),
      description
    };

    return this.updateAllPositionFieldsMutation
      .mutate(variables, { fetchPolicy: 'no-cache' })
      .pipe<Quote, Offer, Offer>(
        map((r) => r.data?.custom?.updateQuote as Quote),
        map((quote: Quote) => this.offerService.mapQuoteToOffer(quote)),
        take(1)
      );
  }

  private getCustomLineDescriptionPropertyKey (
    type?: string
  ): LineCustomPropertyKeyType | null {
    switch (type) {
      case LineSubtypePropVal.FREE_ARTICLE:
        return LineCustomPropertyKeyType.FreieArtikelDescription;
      case LineSubtypePropVal.MY_ARTICLE:
        return LineCustomPropertyKeyType.MeineArtikelDescription;
      default:
        return null;
    }
  }

  setSelectorPosition(offer: OfferBase, productSelector: ProductSelector): Observable<SelectorLine | undefined> {
    return this.addSelectorPosition(offer, productSelector);
  }

  addSelectorPosition(offer: OfferBase, productSelector: ProductSelector): Observable<SelectorLine> {
    return this.addProductPositionCustom(offer, productSelector.product || '', true).pipe<SelectorLine, SelectorLine>(
      concatMap((posId: string) => this.getSelectorPosition(offer, posId)),
      take(1)
    );
  }

  getSelectorPosition(offer: OfferBase, posId: string): Observable<SelectorLine> {
    const variables = {
      documentVersion: extractDocVersion(offer),
      lineId: posId,
      withConfiguration: true,
      datamodelVersionId: this.datamodel.version,
      filtersId: this.datamodel.filters,
      frameImageId: this.datamodel.frameImageId,
      vtzFileId: this.datamodel.vtzFile,
      elementFilterId: this.datamodel.element,
      typeFilterId: this.datamodel.type
    };

    return this.selectorPositionQuery.fetch(variables, { fetchPolicy: 'no-cache' }).pipe<SelectorLine, SelectorLine>(
      map((r) => r.data?.quotes?.quote?.line as unknown as SelectorLine),
      take(1)
    );
  }

  getSelectorFilterQuery(offer: OfferBase, posId: string): Observable<SelectorLine> {
    const variables = {
      documentVersion: extractDocVersion(offer),
      lineId: posId
    };

    return this.selectorFilterQuery.fetch(variables, { fetchPolicy: 'no-cache' }).pipe<SelectorLine, SelectorLine>(
      map((r) => r.data?.quotes?.quote?.line as unknown as SelectorLine),
      take(1)
    );
  }

  addProductPosition(offer: OfferBase, productId: string, setOptional?: boolean): Observable<string> {
    const variables = {
      documentVersion: extractDocVersion(offer),
      productId
    };

    return this.addProductPositionMutation
      .mutate(variables, { fetchPolicy: 'no-cache' })
      .pipe<ProductLine[], string, string>(
        map((r) => r.data?.quotes?.modify?.addProductLine?.newLines as ProductLine[]),
        concatMap((newLines) => {
          const newLine = newLines[0];

          if (setOptional) {
            if (!this.posCustomPropertiesService.isOptionalLine(newLine)) {
              return this.setOptionalPositions(offer, [newLine.id]).pipe(map(() => newLine.id));
            }

            return of(newLine.id);
          }

          return of(newLine.id);
        }),
        take(1)
      );
  }

  addProductPositionCustom(offer: OfferBase, productId: string, setOptional?: boolean): Observable<string> {
    const variables = {
      documentVersion: extractDocVersion(offer),
      productId
    };

    return this.addProductPositionCustomMutation
    .mutate(variables, { fetchPolicy: 'no-cache' })
    .pipe<ProductLine[], string, string>(
      map((r) => r.data?.custom?.customAddProductLine?.newLines as ProductLine[]),
      concatMap((newLines) => {
        const newLine = newLines[0];

        if (setOptional) {
          if (!this.posCustomPropertiesService.isOptionalLine(newLine)) {
            return this.setOptionalPositions(offer, [newLine.id]).pipe(map(() => newLine.id));
          }

          return of(newLine.id);
        }

        return of(newLine.id);
      }),
      take(1)
    );
  }

  // not in use at the moment
  addHeading(offer: OfferBase, productId: string): Observable<string> {
    const variables = {
      documentVersion: extractDocVersion(offer),
      title: productId
    };

    return this.addHeadingMutation.mutate(variables, { fetchPolicy: 'no-cache' }).pipe<HeaderLine[], string, string>(
      map((r) => r.data?.quotes?.modify?.addHeaderLine?.newLines as HeaderLine[]),
      map((lines: HeaderLine[]) => lines[lines.length - 1].id),
      take(1)
    );
  }

  copyPositionMiniCart(offer: OfferBase, positionId: string): Observable<ProductLine[]> {
    return this.copyMiniCartPosition(offer, positionId).pipe(
      concatMap((newPositions: ProductLine[]) =>
        combineLatest(
          newPositions.map((newPosition) =>
            this.pricingService.ensurePricesUpdated(offer, newPosition.id).pipe(
              map((o) => ({
                ...newPosition,
                pricing: (o && o.line && o.line.pricing) || undefined
              }))
            )
          )
        ).pipe(
          take(1) // TODO: Test this since forkJoin was changed to combineLatest
        )
      )
    );
  }

  private copyMiniCartPosition(offer: OfferBase, positionId: string): Observable<ProductLine[]> {
    return this.copyPositions(offer, [positionId], MassActionsMod.MiniCart).pipe(
      map((newPositions) => newPositions as ProductLine[])
    );
  }

  copyPositions(
    offer: OfferBase,
    positionIds: string[],
    actionType: MassActionsMod = MassActionsMod.Base,
    resetDiscounts = false,
    parentLineId?: string // Used only for single line copies to specify the parent line.
  ): Observable<ProductLine[]> {
    const documentVersion: DocumentVersionInput = extractDocVersion(offer);
    const isMiniCartCopy = actionType === MassActionsMod.MiniCart;
    const isMassCopy = actionType === MassActionsMod.MassOperations;
    const variables = {
      documentVersion,
      lineIds: positionIds,
      withPricing: !isMiniCartCopy,
      withFilters: isMiniCartCopy
    };

    return this.copyPositionsCustomMutation.mutate(variables, { fetchPolicy: 'no-cache' }).pipe(
      map(
        (r) =>
          r.data?.custom?.customCopyLines as {
            newLines: ProductLine[];
            quote: Quote;
          }
      ),
      concatMap(({ newLines, quote }) => {
        if (!isMiniCartCopy && resetDiscounts) {
          return this.processPositionsResetDiscounts(resetDiscounts, documentVersion, quote, newLines);
        }

        return of({ newLines, quote });
      }),
      concatMap(({ newLines, quote }) => {
        if (isMiniCartCopy) {
          const standardLinesIds = newLines
          .filter((p) => !this.posCustomPropertiesService.isOptionalLine(p) && !isBomSubLine(p))
          .map((p) => p.id);

          if (standardLinesIds.length) {
            // set mini-cart position as optional so that is hidden until confirmed,
            // and its price is not added to the quote total
            return this.setOptionalPositions(offer, standardLinesIds).pipe(map(() => ({ newLines, quote })));
          }

          // update store positions
          return of({ newLines, quote });
        }

        let relations: { id: string; value: string }[];

        if (isMassCopy) {
          let value;
          const offerGroups = (offer as Offer).productPositions;
          const lastGroupIndex = offerGroups.length - 1;
          const linesArr = newLines.filter((p) => !isBomSubLine(p));

          relations = linesArr.reduce((arr: { id: string; value: string }[], line: CustomLine | ProductLine, index) => {
            value = SnkLineId.Parent;
            if (isGroupableLine(line) && !isExternalArticleLine(line)) {
              if (index === 0) {
                const parentLine = offerGroups[lastGroupIndex].positions[0] as ProductLine;
                if (this.getProductName(parentLine) === getLineProductName(line)) {
                  value = parentLine.id;
                }
              } else {
                if (
                  !isExternalArticleLine(linesArr[index - 1]) &&
                  isGroupableLine(linesArr[index - 1]) &&
                  getLineProductName(linesArr[index - 1]) === getLineProductName(line)
                ) {
                  value = arr[index - 1].value === SnkLineId.Parent ? arr[index - 1].id : arr[index - 1].value;
                }
              }
            }

            return [...arr, { id: line.id, value }];
          }, []);
        } else {
          relations = parentLineId
            ? // base duplication for subposition in calculation sheet
            newLines.map((newLine) => ({
              id: newLine.id,
              value: parentLineId
            }))
            : // base duplication for product positions in calculation sheet
            [
              { id: newLines[0].id, value: SnkLineId.Parent },
              ...newLines.slice(1).map((l) => ({ id: l.id, value: newLines[0].id })) // child positions
            ];
        }

        // Update local state for the new lines to match the new relations
        newLines.forEach((line) => {
          const rel = relations.find((relation) => relation.id === line.id);
          if (rel) {
            setCustomPropertyValueImmutable(line, LineCustomPropertyKeyType.LineID, rel.value);
          }
        });

        return this.setParentChildRelations(offer, relations).pipe(map(() => ({ newLines, quote })));
      }),
      concatMap(({ newLines, quote }) =>
        combineLatest(
          newLines
          .filter((pos) => !isBomSubLine(pos))
          .map((pos: ProductLine) => this.mapPosition(pos))
          .map((pos) => {
            if (
              pos.lineSubtype === LineSubtypePropVal.FREE_ARTICLE
              || ('article' in pos && pos.article)
              || ('articleCustom' in pos && pos.articleCustom)
            ) {
              return {
                ...pos,
                product: this.offerPositionProductService.getProductBaseFromLine(pos)
              };
            }

            return pos;
          })
          .map((pos) => {
            let parentPosition = { ...newLines[0] };
            // For MassCopy we need to set the correct parent position
            // for have correct assignments on product and position level
            if (isMassCopy) {
              const posLineId = this.posCustomPropertiesService.getLineId(pos as ProductLine);
              if (posLineId === SnkLineId.Parent) {
                parentPosition = { ...pos as ProductLine };
              } else {
                parentPosition = newLines.find((p) => p.id === posLineId) as ProductLine;
              }
            }

            return this.mapProductAndPositionAssignments(pos as ProductLine, parentPosition);
          })
          .map((pos: ProductLine) => {
            let position = { ...pos };
            let subPositions = newLines.filter(
              (p) => isBomSubLine(p) && p.parentLine?.id === pos.id
            ) as ProductLine[];

            if (subPositions) {
              position.subLines = subPositions;
            }

            return position;
          })
          .map((pos: ProductLine) => {
            const valueTextType: ValueTextType = isMiniCartCopy
              ? ValueTextType.ConfigurationPosDetails
              : ValueTextType.CalculationSheet;

            return this.valueTextsService.mapValueTextsToProductLine(pos, valueTextType);
          })
        ).pipe(
          map((res) => ({ mappedNewLines: res, quote })),
          take(1) // TODO: Test this since forkJoin was changed to combineLatest
        )
      ),
      map(({ mappedNewLines, quote }) => {
        this.storeService.setOfferUpdate({
          offerPricingUpdate: quote.pricing,
          status: quote.status,
          snkModifier: this.offerMapperService.mapSnkModifier(quote)
        });

        return mappedNewLines;
      }),
      take(1)
    );
  }

  copyPositionsCustom(
    offer: OfferBase,
    positionIds: string[],
    actionType: MassActionsMod = MassActionsMod.Base,
    parentLineId?: string // Used only for single line copies to specify the parent line.
  ): Observable<ProductLine[]> {
    const documentVersion: DocumentVersionInput = extractDocVersion(offer);
    const isMiniCartCopy = actionType === MassActionsMod.MiniCart;
    const isMassCopy = actionType === MassActionsMod.MassOperations;
    const variables = {
      documentVersion,
      lineIds: positionIds,
      withPricing: !isMiniCartCopy,
      withFilters: isMiniCartCopy
    };

    return this.copyPositionsCustomMutation.mutate(variables, { fetchPolicy: 'no-cache' }).pipe(
      map(
        (r) =>
          r.data?.custom?.customCopyLines as {
            newLines: ProductLine[];
            quote: Quote;
          }
      ),
      concatMap(({ newLines, quote }) => {
        if (isMiniCartCopy) {
          const standardLinesIds = newLines
          .filter((p) => !this.posCustomPropertiesService.isOptionalLine(p) && !isBomSubLine(p))
          .map((p) => p.id);

          if (standardLinesIds.length) {
            // set mini-cart position as optional so that is hidden until confirmed,
            // and its price is not added to the quote total
            return this.setOptionalPositions(offer, standardLinesIds).pipe(map(() => ({ newLines, quote })));
          }

          // update store positions
          return of({ newLines, quote });
        }

        let relations: { id: string; value: string }[];

        if (isMassCopy) {
          let value;
          const offerGroups = (offer as Offer).productPositions;
          const lastGroupIndex = offerGroups.length - 1;
          const linesArr = newLines.filter((p) => !isBomSubLine(p));

          relations = linesArr.reduce((arr: { id: string; value: string }[], line: CustomLine | ProductLine, index) => {
            value = SnkLineId.Parent;
            if (isGroupableLine(line) && !isExternalArticleLine(line)) {
              if (index === 0) {
                const parentLine = offerGroups[lastGroupIndex].positions[0] as ProductLine;
                if (this.getProductName(parentLine) === getLineProductName(line)) {
                  value = parentLine.id;
                }
              } else {
                if (
                  !isExternalArticleLine(linesArr[index - 1]) &&
                  isGroupableLine(linesArr[index - 1]) &&
                  getLineProductName(linesArr[index - 1]) === getLineProductName(line)
                ) {
                  value = arr[index - 1].value === SnkLineId.Parent ? arr[index - 1].id : arr[index - 1].value;
                }
              }
            }

            return [...arr, { id: line.id, value }];
          }, []);
        } else {
          relations = parentLineId
            ? // base duplication for subposition in calculation sheet
            newLines.map((newLine) => ({
              id: newLine.id,
              value: parentLineId
            }))
            : // base duplication for product positions in calculation sheet
            [
              { id: newLines[0].id, value: SnkLineId.Parent },
              ...newLines.slice(1).map((l) => ({ id: l.id, value: newLines[0].id })) // child positions
            ];
        }

        // Update local state for the new lines to match the new relations
        newLines.forEach((line) => {
          const rel = relations.find((relation) => relation.id === line.id);
          if (rel) {
            setCustomPropertyValueImmutable(line, LineCustomPropertyKeyType.LineID, rel.value);
          }
        });

        return this.setParentChildRelations(offer, relations).pipe(map(() => ({ newLines, quote })));
      }),
      concatMap(({ newLines, quote }) =>
        combineLatest(
          newLines
          .filter((pos) => !isBomSubLine(pos))
          .map((pos: ProductLine) => this.mapPosition(pos))
          .map((pos) => {
            if (
              pos.lineSubtype === LineSubtypePropVal.FREE_ARTICLE
              || pos.lineSubtype === LineSubtypePropVal.MY_ARTICLE
            ) {
              return {
                ...pos,
                product: this.offerPositionProductService.getProductBaseFromLine(pos)
              };
            }

            return pos;
          })
          .map((pos) => {
            let parentPosition = { ...newLines[0] };
            // For MassCopy we need to set the correct parent position
            // for have correct assignments on product and position level
            if (isMassCopy) {
              const posLineId = this.posCustomPropertiesService.getLineId(pos as ProductLine);
              if (posLineId === SnkLineId.Parent) {
                parentPosition = { ...pos as ProductLine };
              } else {
                parentPosition = newLines.find((p) => p.id === posLineId) as ProductLine;
              }
            }

            return this.mapProductAndPositionAssignments(pos as ProductLine, parentPosition);
          })
          .map((pos: ProductLine) => {
            let position = { ...pos };
            let subPositions = newLines.filter(
              (p) => isBomSubLine(p) && p.parentLine?.id === pos.id
            ) as ProductLine[];

            if (subPositions) {
              position.subLines = subPositions;
            }

            return position;
          })
          .map((pos: ProductLine) => {
            const valueTextType: ValueTextType = isMiniCartCopy
              ? ValueTextType.ConfigurationPosDetails
              : ValueTextType.CalculationSheet;

            return this.valueTextsService.mapValueTextsToProductLine(pos, valueTextType);
          })
        ).pipe(
          map((res) => ({ mappedNewLines: res, quote })),
          take(1) // TODO: Test this since forkJoin was changed to combineLatest
        )
      ),
      concatMap(({ mappedNewLines, quote }) =>
        this.setPositionNumbers().pipe(
          map(() => ({ mappedNewLines, quote }))
        )
      ),
      map(({ mappedNewLines, quote }) => {
        this.storeService.setOfferUpdate({
          offerPricingUpdate: quote.pricing,
          status: quote.status,
          snkModifier: this.offerMapperService.mapSnkModifier(quote)
        });

        return mappedNewLines;
      }),
      take(1)
    );
  }

  processPositionsResetDiscounts(
    resetDiscounts: boolean,
    documentVersion: DocumentVersionInput,
    quote: Quote,
    newLines: ProductLine[]
  ): Observable<{
    newLines: ProductLine[];
    quote: Quote;
  }> {
    let updateQuote = { ...quote };
    let updateLines = [ ...newLines ];

    const discountsObs: Observable<any>[] = [];

    if (resetDiscounts) {
      newLines.forEach((line) => {
        if (isProductLine(line)) {
          const lineObs = this.pricingService.getResetPositionDiscountsObservables(
            documentVersion,
            line
          );

          if (lineObs.length) {
            discountsObs.push(...lineObs);
          }
        }
      });
    }

    if (discountsObs.length) {
      return from(discountsObs).pipe(
        concatMap(obs => obs),
        last(),
        take(1),
        map((res: { pricing: OfferPricing, lines: (CustomLine | ProductLine)[] }) => {
          updateQuote.pricing = res.pricing;

          updateLines = newLines.map((line) => {
            const updatedLine = res?.lines.find((l) => l.id === line.id);

            if (updatedLine) {
              line.pricing = (updatedLine as ProductLine).pricing;
            }

            return line;
          });

          return {
            newLines: updateLines,
            quote: updateQuote
          };
        })
      )
    }

    return of({
      newLines: updateLines,
      quote: updateQuote
    });
  }

  addNewProductToOffer(offer: OfferBase, positionId: string, sources?: ImportLinesInput[]): Observable<string> {
    const isImport = Array.isArray(sources) && sources.length > 0;

    const creation$ = isImport
      ? // there was already a line created for free configuration and the filters
        this.deleteOldAndImportNewPostion(offer, positionId, sources).pipe(
          concatMap((posId) =>
            this.setCustomPropertyForLine(offer, posId, LineCustomPropertyKeyType.ArticleID, '10').pipe(
              map(() => posId)
            )
          ),
          concatMap((posId) => this.setOptionalPositions(offer, [posId]).pipe(map(() => posId)))
        )
      : // reuse existing free configuration line
        of(positionId);

    return creation$.pipe(
      concatMap((lineId: string) =>
        lineId && lineId.length
          ? this.pricingService.ensurePricesUpdated(offer, lineId).pipe(map(() => lineId))
          : of(lineId)
      )
    );
  }

  deleteOldAndImportNewPostion(
    offer: OfferBase,
    positionId: string,
    sources: ImportLinesInput[]
    // setMiniCartPositions: boolean
  ): Observable<string> {
    return this.deletePositions(offer, [positionId]).pipe(
      concatMap(() =>
        this.offerService.importLines(offer, sources).pipe(
          map((lineIds) => (Array.isArray(lineIds) && lineIds.length > 0 && lineIds[0] && lineIds[0].id) || ''),
          take(1)
        )
      )
    );
  }

  updateSelectorAssignment(
    offer: OfferBase,
    positionId: string,
    variableAssignment: VariableAssignmentInput,
    withConfiguration = true
  ): Observable<SelectorLine> {
    const variables = {
      documentVersion: extractDocVersion(offer),
      lineId: positionId,
      variableAssignment,
      withConfiguration,
      datamodelVersionId: this.datamodel.version,
      filtersId: this.datamodel.filters,
      frameImageId: this.datamodel.frameImageId,
      vtzFileId: this.datamodel.vtzFile,
      elementFilterId: this.datamodel.element,
      typeFilterId: this.datamodel.type
    };

    return this.updateSelectorAssignmentMutation
      .mutate(variables, { fetchPolicy: 'no-cache' })
      .pipe<SelectorLine, SelectorLine>(
        map((r) => r.data?.quotes?.modify?.line?.configuration?.assign?.line as unknown as SelectorLine),
        take(1)
      );
  }

  updateSelectorAssignments(
    offer: OfferBase,
    positionId: string,
    variableAssignments: VariableAssignmentInput[],
    withConfiguration = true
  ): Observable<SelectorLine> {
    const variables = {
      documentVersion: extractDocVersion(offer),
      lineId: positionId,
      variableAssignments,
      withConfiguration,
      datamodelVersionId: this.datamodel.version,
      filtersId: this.datamodel.filters,
      frameImageId: this.datamodel.frameImageId,
      vtzFileId: this.datamodel.vtzFile,
      elementFilterId: this.datamodel.element,
      typeFilterId: this.datamodel.type
    };

    return this.updateSelectorAssignmentsMutation
      .mutate(variables, { fetchPolicy: 'no-cache' })
      .pipe<SelectorLine, SelectorLine>(
        map((r) => r.data?.quotes?.modify?.line?.configuration?.setAssignments?.line as unknown as SelectorLine),
        take(1)
      );
  }

  updateLineAssignments(
    offer: OfferBase,
    lineId: string,
    variableAssignments: VariableAssignmentInput[]
  ): Observable<string> {
    return this.updateLineAssignmentsMutation
      .mutate(
        {
          documentVersion: extractDocVersion(offer),
          lineId,
          variableAssignments
        },
        { fetchPolicy: 'no-cache' }
      )
      .pipe<string, string>(
        map(() => lineId),
        take(1)
      );
  }

  setCustomPropertyForLine(
    offer: OfferBase,
    lineId: string,
    key: LineCustomPropertyKeyType,
    value: string
  ): Observable<string | null> {
    const variables = {
      documentVersion: extractDocVersion(offer),
      lineId,
      key,
      stringValue: value
    };

    return this.setCustomPropertyForLineMutation.mutate(variables, { fetchPolicy: 'no-cache' }).pipe(
      map((r) => r.data?.quotes?.modify?.line?.setCustomPropertyValue.id as string),
      catchError((err) => {
        console.error('Error on setCustomPropertyForLine:', err);

        return of(null);
      }),
      take(1)
    );
  }

  deletePositions(offer: OfferBase, deletePositionIds: string[], withPricing = true): Observable<Offer | null> {
    const documentVersion: DocumentVersionInput = extractDocVersion(offer);
    const variables = {
      documentVersion,
      lineIds: deletePositionIds,
      withPricing
    };

    // TODO: when deleting a parent line from mini-cart and exiting the configuration page,
    // we need to update the child lines to a new parent (first child).
    // Suggestion: to be solved by normalizing the lines when opening an offer,
    // or manual action by clicking a button

    return this.deletePositionsMutation.mutate(variables, { fetchPolicy: 'no-cache' }).pipe(
      map((r) => r.data?.quotes?.modify?.deleteLines as { quote: Quote }),
      map((res: { quote: Quote }) => this.offerService.mapQuoteToOffer(res.quote)),
      concatMap((o) => this.setPositionNumbers().pipe(map(() => o))),
      take(1),
      catchError((error) => {
        console.error('Error on deletePositions:', error);

        return of(null);
      })
    );
  }

  mapPosition(position: ProductLine | CustomLine, mapAssignments = true): ProductLine | CustomLine {
    let positionNumber;
    let descriptiveText;
    let optionalOrAlternative = false;
    let lineSubtype;

    if (position) {
      if (position.customProperties) {
        positionNumber = this.posCustomPropertiesService.getPositionNumber(position);
        descriptiveText = this.posCustomPropertiesService.getDescriptiveText(position);
        optionalOrAlternative = this.posCustomPropertiesService.isOptionalOrAlternativeLine(position);
        lineSubtype = this.posCustomPropsService.getLineSubtype(position);
      }

      if ('configuration' in position && position.configuration) {
        let pos = this.mapPositionAssignments(position, mapAssignments);

        if (pos.customProperties) {
          pos = {
            ...pos,
            positionNumber,
            descriptiveText,
            optionalOrAlternative,
            lineSubtype
          };
        }

        return pos;
      }

      // Map product name for inactive positions
      let productLine = { ...position } as ProductLine;
      if (productLine.product) {
        productLine.product = this.offerPositionProductService.getProductBaseFromLine(productLine) as SnkProduct;
        productLine.product.productName = getLineProductName(productLine);
      }

      return {
        ...position,
        product: productLine.product,
        positionNumber,
        descriptiveText,
        optionalOrAlternative,
        lineSubtype
      } as ProductLine;
    }

    return position;
  }

  private mapProductAndPositionAssignments(position: ProductLine, parent?: ProductLine): ProductLine {
    const pos = { ...position } as ProductLine;

    if ('configuration' in pos && pos.configuration) {
      // eslint-disable-next-line no-underscore-dangle
      if (pos.configuration.sections && pos.__typename === 'ProductLine') {
        (pos.product as SnkProduct) = this.offerPositionProductService.mapFilteredVariables(pos, parent);
      }
    }

    return pos;
  }

  private mapPositionAssignments(pos: ProductLine, mapAssignments: boolean): ProductLine {
    const position = { ...pos };

    // flatten sections
    if (mapAssignments && position.configuration) {
      position.configuration.sections = (position.configuration.sections || []).map((section: ConfigurationSection) => {
        let items: ConfigurationVariable[] = [];
        let sections: ConfigurationSection[] = [];

        // extract Filter sections for configuration page
        if (section.id === this.datamodel.filters) {
          sections = section.sections;
        }

        // extract variables from section and its sub-sections
        if (section.variables && section.variables.items) {
          items = section.variables.items as ConfigurationVariable[];
        } else if (section.sections) {
          items = section.sections
            .map((s) => (s.variables && s.variables.items) || [])
            .flat() as ConfigurationVariable[];
        }

        items = items.map((item) => mapConfigurationVariable(item));

        const show = this.offerSectionService.isSectionVisible(section);

        return {
          id: section.id,
          name: section.name,
          show,
          variables: { items },
          sections
        } as ConfigurationSection;
      });
    }

    return { ...position };
  }

  addCustomPositionToOffer(offer: OfferBase): Observable<CustomLine[]> {
    const variables = {
      documentVersion: extractDocVersion(offer)
    };

    return this.addCustomPositionMutation
      .mutate(variables, { fetchPolicy: 'no-cache' })
      .pipe<CustomLine[], CustomLine[], CustomLine[]>(
        map((r) => r.data?.quotes?.modify?.addCustomLine?.newLines as CustomLine[]),
        concatMap((newLines: CustomLine[]) => this.setPositionNumbers().pipe(map(() => newLines))),
        take(1)
      );
  }

  addCustomPositionCustomToOffer(offer: OfferBase): Observable<CustomLine[]> {
    const variables = {
      documentVersion: extractDocVersion(offer)
    };

    return this.addCustomPositionCustomMutation
    .mutate(variables, { fetchPolicy: 'no-cache' })
    .pipe<CustomLine[], CustomLine[], CustomLine[]>(
      map((r) => r.data?.custom?.customAddCustomLine?.newLines as CustomLine[]),
      concatMap((newLines: CustomLine[]) => this.setPositionNumbers().pipe(map(() => newLines))),
      take(1)
    );
  }

  updatePositionCustomProperties(offer: OfferBase, positions: LineInputType[]): Observable<Offer> {
    return this.offerService.updateOffer(offer, undefined, positions);
  }

  toggleAlternativePositions(
    offer: Offer | OfferBase,
    lineIds: string[],
    withPricing = true
  ): Observable<Partial<Quote>> {
    return this.togglePositionsAlternative(offer as OfferBase, lineIds, withPricing).pipe(
      map((quote) => {
        let lines;
        if (quote.lines) {
          lines = quote.lines
            .filter((line) => lineIds.includes(line.id))
            .map((line) => {
              let productLine = { ...line };
              productLine.optionalOrAlternative = this.posCustomPropertiesService.isOptionalOrAlternativeLine(line);

            return productLine;
          });
        }

        return {
          ...quote,
          lines
        };
      })
    );
  }

  private togglePositionsAlternative(
    offer: OfferBase,
    lineIds: string[],
    withPricing?: boolean
  ): Observable<Quote> {
    const variables = {
      documentVersion: extractDocVersion(offer),
      lineIds,
      withPricing
    };

    // This mutation intentionally returns only line IDs,
    // in order to reduce the response size to minumum,
    // which is crucial (performance wise) when the quote has many lines.
    return this.toggleAlternativePositionMutation
      .mutate(variables, { fetchPolicy: 'no-cache' })
      .pipe(map((r) => r.data?.custom?.toggleAlternative as Quote));
  }

  applyFreeProductPositionUpdate(
    productPositions: OfferProductPosition[],
    updateData: OfferProductPosition
  ): OfferProductPosition[] {
    const groupIndex = this.findProductGroupIndexByPositionId(productPositions, updateData.positions[0].id);
    const result = productPositions;
    result[groupIndex] = updateData;

    return result;
  }

  getMaterialAndEanCodes(offer: Offer, lineIds: string[]): Observable<MaterialAndEanOutput[]> {
    const variables = {
      documentVersion: extractDocVersion(offer),
      lineIds
    };

    return this.materialAndEanCodesQuery.fetch(variables, { fetchPolicy: 'no-cache' }).pipe(
      map((r) => r.data?.materialAndEanCodes as MaterialAndEanOutput[]),
      take(1)
    );
  }

  linesToProductPositions(lines: Array<CustomLine | ProductLine>, parentId?: string) {
    let firstGroup;
    let newLines = lines.filter((pos) => !isBomSubLine(pos));
    if (parentId) {
      newLines = newLines.filter(
        (pos) => this.posCustomPropsService.getLineId(pos) !== parentId && pos.id !== parentId
      );
    }

    const parentPositions = newLines.filter(
      (pos) =>
        (this.posCustomPropsService.isParentLine(pos) || this.posCustomPropsService.isDetachedLine(pos)) &&
        !isBomSubLine(pos)
    );
    if (parentId) {
      const firstLines = lines.filter(
        (pos) => this.posCustomPropsService.getLineId(pos) === parentId || pos.id === parentId
      );
      if (firstLines.length) {
        firstGroup = {
          product: this.offerPositionProductService.getProductBaseFromLine(firstLines[0]),
          positions: firstLines
        };
      }
    }

    const productGroups = parentPositions.reduce(
      (prGroups: OfferProductPosition[], parentLine: CustomLine | ProductLine) => {
        if (!isGroupableLine(parentLine)) {
          return [
            ...prGroups,
            {
              product: this.offerPositionProductService.getProductBaseFromLine(parentLine),
              positions: [parentLine]
            }
          ];
        }

        const childLines = newLines.filter(
          (p) => this.posCustomPropsService.getLineId(p) === parentLine.id && !isBomSubLine(p)
        ) as ProductLine[];
        const positions = this.sortPositions([parentLine, ...childLines]);

        return [
          ...prGroups,
          {
            product: this.offerPositionProductService.getProductBaseFromLine(positions[0]),
            positions
          }
        ];
      },
      []
    );

    if (firstGroup) {
      productGroups.splice(0, 0, firstGroup);
    }

    return productGroups;
  }
}
